import React from 'react';
import { FiBookOpen, FiShield, FiTarget, FiTrendingUp, FiUsers, FiZap } from 'react-icons/fi';
import { useLanguage } from '../components';
import ComplianceNotice from '../components/ComplianceNotice';
import './Mission.css';

const CONTENT = {
  en: {
    heroTitle: 'Financial Planning Hub Mission',
    heroBody:
      'Our planning mission is to make Canadian financial decisions easier to understand through transparent assumptions, beginner-friendly education, and practical scenario modeling.',
    missionTitle: 'Planning Promise',
    missionStatement:
      'Help households understand choices around registered accounts, goals, growth, and retirement with clear numbers, clear limits, and clear trade-offs.',
    valuesTitle: 'What This Hub Prioritizes',
    footnote:
      'Financial Planning Hub content is educational and scenario-based. It is not personalized financial, legal, or tax advice.',
    values: [
      {
        icon: FiBookOpen,
        title: 'Education Before Action',
        description: 'Explain concepts first so users understand why a recommendation could make sense before they act.',
      },
      {
        icon: FiTarget,
        title: 'Goal Clarity',
        description: 'Turn broad life goals into concrete monthly savings targets, account strategies, and time horizons.',
      },
      {
        icon: FiTrendingUp,
        title: 'Scenario Transparency',
        description: 'Show conservative, moderate, and optimistic paths with assumptions visible to the user.',
      },
      {
        icon: FiShield,
        title: 'Compliance by Design',
        description: 'Keep educational boundaries clear and avoid presenting outputs as personalized advice.',
      },
      {
        icon: FiUsers,
        title: 'Beginner Accessibility',
        description: 'Use plain language, explain terms, and reduce interface clutter for first-time planners.',
      },
      {
        icon: FiZap,
        title: 'Actionable Next Steps',
        description: 'Convert complex planning outputs into simple next actions users can review and update yearly.',
      },
    ],
  },
  fr: {
    heroTitle: 'Mission du Centre de planification financière',
    heroBody:
      'Notre mission de planification est de rendre les décisions financières canadiennes plus simples à comprendre grâce à des hypothèses transparentes, une éducation accessible et une modélisation pratique de scénarios.',
    missionTitle: 'Promesse de planification',
    missionStatement:
      'Aider les ménages à comprendre leurs choix liés aux comptes enregistrés, aux objectifs, à la croissance et à la retraite avec des chiffres clairs, des limites claires et des compromis clairs.',
    valuesTitle: 'Priorités du centre',
    footnote:
      'Le contenu du Centre de planification financière est éducatif et basé sur des scénarios. Il ne constitue pas un conseil financier, juridique ou fiscal personnalisé.',
    values: [
      {
        icon: FiBookOpen,
        title: 'Éducation avant action',
        description: 'Expliquer les concepts d’abord pour que les utilisateurs comprennent pourquoi une stratégie peut être pertinente.',
      },
      {
        icon: FiTarget,
        title: 'Clarté des objectifs',
        description: 'Transformer des objectifs de vie généraux en cibles d’épargne mensuelle, stratégies de compte et horizons clairs.',
      },
      {
        icon: FiTrendingUp,
        title: 'Transparence des scénarios',
        description: 'Présenter des trajectoires prudentes, modérées et optimistes avec des hypothèses visibles.',
      },
      {
        icon: FiShield,
        title: 'Conformité intégrée',
        description: 'Maintenir des limites éducatives claires et éviter de présenter les résultats comme des conseils personnalisés.',
      },
      {
        icon: FiUsers,
        title: 'Accessibilité débutant',
        description: 'Utiliser un langage simple, expliquer les termes et réduire la surcharge visuelle pour les nouveaux utilisateurs.',
      },
      {
        icon: FiZap,
        title: 'Prochaines actions concrètes',
        description: 'Convertir des résultats complexes en actions simples à revoir et ajuster chaque année.',
      },
    ],
  },
};

const PlanningMission = () => {
  const { lang } = useLanguage();
  const c = CONTENT[lang] || CONTENT.en;

  return (
    <div className="mission-root">
      <section className="mission-hero mission-glass">
        <div className="mission-hero-icon"><FiTarget /></div>
        <h1>{c.heroTitle}</h1>
        <p>{c.heroBody}</p>
      </section>

      <ComplianceNotice compact />

      <section className="mission-statement mission-glass">
        <h2>{c.missionTitle}</h2>
        <blockquote>{c.missionStatement}</blockquote>
      </section>

      <section className="mission-values-section">
        <h3>{c.valuesTitle}</h3>
        <div className="mission-values-grid">
          {c.values.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.title} className="mission-glass mission-value-card">
                <Icon className="mission-value-icon" />
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      <footer className="mission-footnote">{c.footnote}</footer>
    </div>
  );
};

export default PlanningMission;
